import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ChildTableColumn } from '../../models/child-table.models';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';
import { ChildTablePositioningService } from '../../services/child-table-positioning.service';

@Component({
  selector: 'child-table-cell-editor',
  templateUrl: './child-table-cell-editor.component.html',
  styleUrls: ['./child-table-cell-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildTableCellEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;

  @Input() column!: ChildTableColumn<any>;
  @Input() value: any;
  @Input() rowId = '';
  @Input() editHintId = 'edit-hint';

  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() tabSave = new EventEmitter<{ value: any; shiftKey: boolean }>();

  editingValue: any;
  listboxHighlightIndex = 0;

  private typeAheadBuffer = '';
  private typeAheadTimer: any = null;

  constructor(
    private readonly a11yService: ChildTableA11yService,
    private readonly cdr: ChangeDetectorRef,
    private readonly positioningService: ChildTablePositioningService,
  ) {}

  ngOnInit(): void {
    this.editingValue = this.value;
    if (this.column.type === 'select' && this.column.selectOptions?.length) {
      const idx = this.column.selectOptions.findIndex((o) => o.value === this.editingValue);
      this.listboxHighlightIndex = idx >= 0 ? idx : 0;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.column.type === 'select') {
        const trigger = this.editInput?.nativeElement?.closest('.cell-listbox-wrapper')
          ?.querySelector('.cell-listbox-trigger') as HTMLElement;
        if (!trigger) return;
        trigger.focus();
        const listbox = trigger.closest('.cell-listbox-wrapper')
          ?.querySelector('.cell-listbox') as HTMLElement;
        if (listbox) {
          listbox.style.minWidth = Math.max(trigger.getBoundingClientRect().width, 180) + 'px';
          this.positioningService.openMenu('cell-select', trigger, listbox, () => {
            this.cancel.emit();
          });
        }
        this.cdr.markForCheck();
      } else if (this.editInput?.nativeElement) {
        this.editInput.nativeElement.focus();
        this.editInput.nativeElement.select();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.positioningService.closeMenu('cell-select');
  }

  getOptionLabel(value: any): string {
    if (!this.column.selectOptions) return String(value ?? '');
    const option = this.column.selectOptions.find((o) => o.value === value);
    return option ? option.label : String(value ?? '');
  }

  selectOption(value: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingValue = value;
    this.save.emit(this.editingValue);
  }

  onSave(): void {
    this.save.emit(this.editingValue);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.save.emit(this.editingValue);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancel.emit();
    }
  }

  onListboxKeydown(event: KeyboardEvent): void {
    const options = this.column.selectOptions;
    if (!options?.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.listboxHighlightIndex = Math.min(this.listboxHighlightIndex + 1, options.length - 1);
        this.scrollOptionIntoView();
        this.a11yService.announce(options[this.listboxHighlightIndex].label);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.listboxHighlightIndex = Math.max(this.listboxHighlightIndex - 1, 0);
        this.scrollOptionIntoView();
        this.a11yService.announce(options[this.listboxHighlightIndex].label);
        break;
      case 'Home':
        event.preventDefault();
        this.listboxHighlightIndex = 0;
        this.scrollOptionIntoView();
        this.a11yService.announce(options[0].label);
        break;
      case 'End':
        event.preventDefault();
        this.listboxHighlightIndex = options.length - 1;
        this.scrollOptionIntoView();
        this.a11yService.announce(options[this.listboxHighlightIndex].label);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.selectOption(options[this.listboxHighlightIndex].value);
        break;
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        this.tabSave.emit({
          value: options[this.listboxHighlightIndex].value,
          shiftKey: event.shiftKey,
        });
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.cancel.emit();
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          this.typeAheadBuffer += event.key.toLowerCase();
          clearTimeout(this.typeAheadTimer);
          this.typeAheadTimer = setTimeout(() => { this.typeAheadBuffer = ''; }, 500);
          const matchIdx = options.findIndex((o) =>
            o.label.toLowerCase().startsWith(this.typeAheadBuffer)
          );
          if (matchIdx >= 0) {
            this.listboxHighlightIndex = matchIdx;
            this.scrollOptionIntoView();
            this.a11yService.announce(options[matchIdx].label);
          }
        }
        break;
    }
    this.cdr.markForCheck();
  }

  private scrollOptionIntoView(): void {
    setTimeout(() => {
      const listbox = document.querySelector('.cell-listbox') as HTMLElement;
      const option = listbox?.querySelector('.listbox-option.highlighted') as HTMLElement;
      if (option && listbox) {
        option.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }
}
